import { GroupsService } from '@client/services/groups';
import { UsersService } from '@client/services/users';

const BINDING_KEYS = [
  'googlegroupbindings',
  'o365groupbindings',
  'oktagroupbindings',
  'csvtreenodebindings',
  'googleorgbindings'
];

const normalize = (email) => (email || '').trim().toLowerCase();

const emailsFrom = (users) => users.map((user) => normalize(user.email || user.username)).filter(Boolean);

// One search per binding so the request does not AND the binding types together.
// The hide flags are off so an inactive member is not dropped before the page's own
// filters get a say. Membership past 10,000 users is silently truncated.
const searchEmails = async (bindings) => {
  const response = await UsersService.getUsers({
    ...bindings,
    page: 1,
    perPage: 10000,
    systemFilter: [],
    hideRemoved: false,
    hideNeverSent: false,
    hideNeverUsed: false,
    hideAddin: null
  });

  if (!Array.isArray(response?.data)) {
    throw new Error('User search failed while resolving group members');
  }

  return emailsFrom(response.data);
};

const bindingEmails = async (source = {}) => {
  const searches = BINDING_KEYS.filter((key) => source[key]?.length).map((key) => searchEmails({ [key]: source[key] }));

  return (await Promise.all(searches)).flat();
};

const dynamicEmails = async (groupname) => {
  const response = await GroupsService.getDynamicMembers({ groupname });

  if (response === 'Empty') {
    return [];
  }

  if (!Array.isArray(response)) {
    throw new Error(`Dynamic membership failed for group ${groupname}`);
  }

  return response.map(normalize).filter(Boolean);
};

/**
 * Resolves an Opensense sender group to the emails of its members.
 *
 * A group is a union of direct senders, directory bindings and a dynamic query,
 * minus its excluded senders and excluded directory groups.
 *
 * @param {Object} group A sender group as returned by GroupsService.getAll.
 * @returns {Promise<string[]>} Lowercased, unique, sorted emails.
 */
export const resolveGroupMembers = async (group) => {
  const senders = group.senders || [];
  const hasAllUsers = senders.some(({ username }) => username === '*');

  const [all, bound, dynamic, excludedBound] = await Promise.all([
    hasAllUsers ? searchEmails({}) : [],
    bindingEmails(group),
    group.userAttrs?.length ? dynamicEmails(group.groupname) : [],
    bindingEmails(group.exclusions)
  ]);

  const excluded = new Set([...excludedBound, ...emailsFrom(group.senderexclusions || [])]);

  const members = new Set(
    [...emailsFrom(senders.filter(({ username }) => username !== '*')), ...all, ...bound, ...dynamic].filter(
      (email) => !excluded.has(email)
    )
  );

  return [...members].sort();
};

/**
 * Folds resolved group members into a users request through its CSV filter.
 *
 * @param {Object} params The users request params.
 * @param {string[]|null} members Emails from resolveGroupMembers, or null for no group.
 * @returns {Object|null} The request to send, or null when nothing can match.
 */
export const applyGroupMembers = (params, members) => {
  if (members === null || members === undefined) {
    return params;
  }

  const csv = new Set((params.csvfilter?.data || []).map(({ username }) => normalize(username)));

  const data = members
    .filter((email) => (csv.size ? csv.has(email) !== !!params.excludeCSV : true))
    .map((username) => ({ username }));

  if (!data.length) {
    return null;
  }

  return {
    ...params,
    excludeCSV: false,
    csvfilter: { ...params.csvfilter, data, errors: [] }
  };
};
